import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  formatLocalTime,
  formatLocalDateTime,
  formatRecordTimeRange,
  getPlaybackRecordList,
  TIMELINE_SCALE_CONFIG,
} from "../utils/playbackHelper";

const PlaybackTimeline = ({
  playbackTime,
  displayPlaybackTime = null,
  timeScale,
  playbackRecords = null,
  timelineRef,
  hoverTimeline = null,
  hoveredRecordKey = null,
  onPointerDown,
  onPointerMove,
  onPointerLeave,
  onRecordHover,
  onRecordLeave,
}) => {
  const theme = useTheme();

  const timelineScaleConfig =
    TIMELINE_SCALE_CONFIG[timeScale] || TIMELINE_SCALE_CONFIG["1H"];

  const timeWindow = timelineScaleConfig.totalMinutes * 60 * 1000;
  // Needle / scale stay on media PTS (aligned with recording bars).
  const centerTime = playbackTime;
  // Digital label may use OSD-compensated time for NVR visual match.
  const labelTime = Number.isFinite(displayPlaybackTime) ? displayPlaybackTime : playbackTime;
  const timelineStart = centerTime - timeWindow / 2;
  const timelineEnd = centerTime + timeWindow / 2;
  const minorTickInterval = timelineScaleConfig.minorSeconds * 1000;
  const majorTickInterval = timelineScaleConfig.majorMinutes * 60 * 1000;

  const timelineTicks = useMemo(() => {
    return Array.from(
      { length: Math.floor(timeWindow / minorTickInterval) + 1 },
      (_, index) => {
        const time = new Date(timelineStart + index * minorTickInterval);
        const elapsed = index * minorTickInterval;
        const isMajor = elapsed % majorTickInterval === 0;
        return {
          key: `${time.getTime()}-${index}`,
          label: isMajor
            ? timeScale === "10M" || timeScale === "1H"
              ? formatLocalTime(time, false)
              : String(time.getHours()).padStart(2, "0")
            : "",
          isMajor,
          position: (elapsed / timeWindow) * 100,
        };
      }
    );
  }, [
    majorTickInterval,
    minorTickInterval,
    timeScale,
    timelineStart,
    timeWindow,
  ]);

  const timelineRecords = useMemo(() => {
    return getPlaybackRecordList(playbackRecords)
      .map((record) => ({
        ...record,
        startTime: Number(record.startTime) * 1000,
        endTime: Number(record.endTime) * 1000,
      }))
      .filter(
        (record) =>
          Number.isFinite(record.startTime) &&
          Number.isFinite(record.endTime) &&
          record.endTime > record.startTime &&
          record.endTime > timelineStart &&
          record.startTime < timelineEnd
      )
      .map((record, index) => {
        const visibleStart = Math.max(record.startTime, timelineStart);
        const visibleEnd = Math.min(record.endTime, timelineEnd);
        return {
          ...record,
          key: `${record.startTime}-${record.endTime}-${index}`,
          left: ((visibleStart - timelineStart) / timeWindow) * 100,
          width: ((visibleEnd - visibleStart) / timeWindow) * 100,
        };
      });
  }, [playbackRecords, timelineEnd, timelineStart, timeWindow]);

  return (
    <Box
      ref={timelineRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      sx={{
        position: "relative",
        height: 84,
        mt: 0.5,
        borderTop: "1px solid #bfc3c8",
        borderBottom: "1px solid #bfc3c8",
        backgroundColor: "#fafafa",
        cursor: "pointer",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      {/* Current Pointer Time Label */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 2,
          transform: "translateX(-50%)",
          zIndex: 4,
          color: theme.palette.primary.main,
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "nowrap",
          userSelect: "none",
        }}
      >
        {formatLocalDateTime(new Date(labelTime))}
      </Box>

      {/* Pointer Arrow */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 25,
          width: 0,
          height: 0,
          transform: "translateX(-50%)",
          borderLeft: "7px solid transparent",
          borderRight: "7px solid transparent",
          borderTop: `7px solid ${theme.palette.primary.main}`,
          zIndex: 4,
        }}
      />

      {/* Hover Single-Time Indicator */}
      {hoverTimeline && Math.abs(hoverTimeline.position - 50) > 1.2 && (
        <Box
          sx={{
            position: "absolute",
            left: `${hoverTimeline.offsetX}px`,
            top: Math.abs(hoverTimeline.position - 50) < 18 ? -22 : 2,
            transform: "translateX(-50%)",
            zIndex: 8,
            px: 0.9,
            py: 0.25,
            borderRadius: "4px",
            backgroundColor: "#f0f4fa",
            border: "1px solid #24336f",
            color: "#172664",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            boxShadow: "0 3px 10px rgba(36, 51, 111, 0.18)",
            pointerEvents: "none",
          }}
        >
          {formatLocalDateTime(new Date(hoverTimeline.time))}
        </Box>
      )}

      {/* Minor & Major Ticks Container */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 32,
          bottom: 23,
          backgroundColor: "#fafafa",
        }}
      >
        {timelineTicks.map((tick) => (
          <Box
            key={tick.key}
            sx={{
              position: "absolute",
              left: `${tick.position}%`,
              top: 0,
              height: tick.isMajor ? 13 : 7,
              borderLeft: `1px solid ${tick.isMajor ? "#8b929c" : "#c7ccd2"}`,
            }}
          />
        ))}
      </Box>

      {/* Record Segments & Hover Badges */}
      {timelineRecords.length > 0 &&
        timelineRecords.map((record) => {
          const badgeLeft = Math.max(2, Math.min(98, record.left + record.width / 2));
          const isHovered = hoveredRecordKey === record.key;
          return (
            <React.Fragment key={record.key}>
              {isHovered && (
                <Box
                  sx={{
                    position: "absolute",
                    left: `${badgeLeft}%`,
                    top: 19,
                    transform: "translateX(-50%)",
                    zIndex: 6,
                    px: 0.9,
                    py: 0.25,
                    borderRadius: "4px",
                    backgroundColor: "#fff7ed",
                    border: "1px solid #f97316",
                    color: "#c2410c",
                    fontSize: 10,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    boxShadow: "0 3px 10px rgba(249, 115, 22, 0.25)",
                    pointerEvents: "none",
                  }}
                >
                  {formatRecordTimeRange(record.startTime, record.endTime)}
                </Box>
              )}
              <Box
                onPointerEnter={(e) => {
                  e.stopPropagation();
                  onRecordHover(record.key);
                }}
                onPointerLeave={(e) => {
                  e.stopPropagation();
                  onRecordLeave();
                }}
                sx={{
                  position: "absolute",
                  left: `${record.left}%`,
                  width: `${record.width}%`,
                  top: 45,
                  bottom: 23,
                  backgroundColor: isHovered ? "#5992c6" : "#73a8d8",
                  borderRadius: "2px",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                  zIndex: 2,
                  "&:hover": {
                    backgroundColor: "#5992c6",
                  },
                }}
              />
            </React.Fragment>
          );
        })}

      {/* Center Line Indicator */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 32,
          bottom: 23,
          width: 2,
          transform: "translateX(-1px)",
          backgroundColor: "#172664",
          zIndex: 2,
          pointerEvents: "none",
        }}
      />

      {/* Major Tick Labels */}
      <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 2, height: 12 }}>
        {timelineTicks
          .filter((tick) => tick.isMajor)
          .map((tick) => (
            <Typography
              key={`${tick.key}-label`}
              sx={{
                position: "absolute",
                left: `${tick.position}%`,
                transform: "translateX(-50%)",
                fontSize: 9,
                color: "#666",
                whiteSpace: "nowrap",
              }}
            >
              {tick.label}
            </Typography>
          ))}
      </Box>
    </Box>
  );
};

PlaybackTimeline.propTypes = {
  playbackTime: PropTypes.number.isRequired,
  displayPlaybackTime: PropTypes.number,
  timeScale: PropTypes.string.isRequired,
  playbackRecords: PropTypes.object,
  timelineRef: PropTypes.object.isRequired,
  hoverTimeline: PropTypes.object,
  hoveredRecordKey: PropTypes.string,
  onPointerDown: PropTypes.func.isRequired,
  onPointerMove: PropTypes.func.isRequired,
  onPointerLeave: PropTypes.func.isRequired,
  onRecordHover: PropTypes.func.isRequired,
  onRecordLeave: PropTypes.func.isRequired,
};

export default PlaybackTimeline;
